// PATH: src/hooks/useSchoolContext.ts — année et classe de travail, partagées.
//
// L'année et la classe sont le contexte de TOUT le travail quotidien (pédagogie,
// paiements, listes d'élèves). Chaque écran les redemandait à l'ouverture :
// l'agent qui suit la 1AP-A toute la matinée la resélectionnait à chaque
// navigation. Elles sont désormais mémorisées et partagées entre les écrans.
import { useCallback, useEffect, useRef, useState } from "react";

const STORAGE_KEY = "tilmischool.context";

interface StoredContext {
  yearId?: number;
  classId?: number;
}

// Lecture défensive : stockage indisponible (navigation privée) ou contenu
// corrompu ne doivent pas empêcher l'application de démarrer.
const read = (): StoredContext => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredContext) : {};
  } catch {
    return {};
  }
};

type NumberSetter = (value: number | undefined | ((prev: number | undefined) => number | undefined)) => void;

export interface SchoolContext {
  yearId?: number;
  classId?: number;
  setYearId: NumberSetter;
  setClassId: NumberSetter;
}

/**
 * useSchoolContext — année et classe de travail, mémorisées d'un écran à l'autre
 * et d'une session à l'autre.
 *
 * Les deux sélecteurs acceptent une fonction de mise à jour (comme useState) :
 * c'est ce qui permet à un écran de valider la valeur restaurée contre la liste
 * qu'il vient de charger, sans dépendre de la valeur courante dans ses effets —
 * `setYearId((prev) => (ys.some((y) => y.id === prev) ? prev : ys[0]?.id))`.
 *
 * Changer d'année VIDE la classe : une classe appartient à une année, la garder
 * afficherait une classe qui n'existe pas dans la nouvelle. La remise à zéro
 * n'a pas lieu au montage — sinon la classe restaurée serait effacée aussitôt.
 */
export const useSchoolContext = (): SchoolContext => {
  const initial = useRef<StoredContext>(read()).current;
  const [yearId, setYearIdState] = useState<number | undefined>(initial.yearId);
  const [classId, setClassIdState] = useState<number | undefined>(initial.classId);

  const previousYear = useRef<number | undefined>(initial.yearId);
  useEffect(() => {
    // `previousYear` vaut la valeur restaurée au premier passage : la condition
    // est donc fausse au montage, et la classe mémorisée survit.
    if (previousYear.current !== undefined && previousYear.current !== yearId) {
      setClassIdState(undefined);
    }
    previousYear.current = yearId;
  }, [yearId]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ yearId, classId }));
    } catch {
      /* préférence non mémorisée, sans conséquence sur la session */
    }
  }, [yearId, classId]);

  const setYearId = useCallback<NumberSetter>((value) => setYearIdState(value as never), []);
  const setClassId = useCallback<NumberSetter>((value) => setClassIdState(value as never), []);

  return { yearId, classId, setYearId, setClassId };
};
